/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Panel as BootstrapPanel } from 'react-bootstrap';
import { Stack } from '@fluentui/react';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { ValidationGroup, ErrorMessages } from '@bingads-webui-react/validation';
import { ServerValidatedWebExclusionsWithValidation, webExclusionPropertyVal } from '@bingads-webui-react/web-exclusions';
import { Campaign } from '@bingads-webui/campaign-odata-schemas';
import _ from 'underscore';
import {
  FormErrorGroup, UnmappedFormErrors,
  entityIs, andComp, propertyIs, anyComp, notComp,
} from '@bingads-webui-campaign-react/form-error';
import { AttentionBeaconGroup } from '@bingads-webui-campaign-react/attention-beacon';
import { ActuationGroup } from '@bingads-webui-campaign-react/actuator';
import { withControlErrorHandling, withUnvalidatedControlErrors } from '@bingads-webui-campaign-react/error-handling';
import { ERROR_ENTITY, getPanelErrorProps, getConvertedCurrencyDealMinimumBid, getAccountExchangeRateFromLocalStorage } from '@bingads-webui-campaign-react/xandr-video-campaign-shared-components';
import { ValidatedLegacyBudget, ValidatedConversionGoals } from '@bingads-webui-campaign-react/step-wizard-controls';
import { getBidTitleDetails } from '@bingads-webui-campaign-react/wizard-shared-components';
import { withStepLoadingShell } from '@bingads-webui-campaign-react/step-wizard';
import { MinXandrDailyBudgetRangePerCurrency } from '@bingads-webui-campaign-legacy/common-grid-config/budget/get-budget-range';
import { Collapse, TextTitle, ValidatedBootstrapPanel, useCollapseState, ValidatedFixedPanel, ValidatedPanel, collapseExpander } from '@bingads-webui-campaign/wizard-jumbo-card';
import {
  isXandrDisplayAdsCampaign,
  isXandrVideoAdsCampaign,
  campaignShouldServeOnMSAN,
  ScopeLevel
} from '@bingads-webui-campaign-react/constants';

import {
  SectionShimmer,
  AudienceBudgetBidStrategy,
} from '@bingads-webui-campaign-react/display-plus-native-ads';
import { ThirdPartyMeasurementV3WithValidation, formatVendorSettings } from '@bingads-webui-campaign-react/third-party-measurement';

import { ImpressionTrackingSetting, formatImpressionTrackingSetting } from './audience-setting/audience-setting';
import { ServerValidatedRemainingCharsValidatedTextField } from '../../common/shared-components';
import { DealImpressionPanel } from '../../common/deal-potential-impression/deal-potential-impression';
import { useStepCampaignSettingsState } from './use-step-campaign-settings-state';
import { getStepCampaignSettingsStyles } from './step-campaign-settings-styles';

const entityIsCampaign = entityIs(ERROR_ENTITY.Campaign);

export const campaignNameProp = andComp(entityIsCampaign, propertyIs('Name'));
const campaignBudgetProp = andComp(entityIsCampaign, propertyIs('Budget'));
const campaignCampaignSettingsProp = andComp(entityIsCampaign, propertyIs('CampaignSettings'));

export const ServerValidatedLegacyBudget = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedLegacyBudget, { filter: campaignBudgetProp }));
export const ValidatedImpressionTrackingSetting = withControlErrorHandling(withUnvalidatedControlErrors(ImpressionTrackingSetting, { filter: campaignCampaignSettingsProp }));

const campaignUrlOptionsProperties = notComp(andComp(
  entityIsCampaign,
  anyComp(
    propertyIs('TrackingUrlTemplate'),
    propertyIs('UrlCustomParameters'),
    propertyIs('FinalUrlSuffix')
  )
));

function StepCampaignSettings(props) {
  const {
    stepService,
    scope: { values: { aid, cid } },
  } = props;

  const classes = useDefaultStyles(getStepCampaignSettingsStyles);
  const [stepState, stepApi] = useStepCampaignSettingsState(stepService);

  const {
    i18n,
    legacyI18n,
    hideStepHeader,
    campaignId,
    campaign,
    campaignErrors,
    navigationConfirmationPrompt,
    serverErrors,
    systemErrors,
    areBeaconsActive,
    showAllValidationErrors,
    serverContext,
    dealDetails,
    campaignSettings,
    exclusions,
  } = stepState;
  const { onCampaignValidation } = stepApi;

  const advertiserCustomerId = serverContext.CurrentAccount.AdvertiserCustomerID;

  const {
    DynamicPermissions: {
      EnableMSANDisplayCampaigns,
      EnableMSANVideoCampaigns,
      EnableDoubleVerify,
    },
    Permissions: {
      IsMSANISPOTEnabled,
    },
  } = serverContext;

  // Deprecate IAS verified impression tracking URLs
  const shouldshowIASVerifiedImpressionTrackingURLs = false;

  const shouldShowThirdPartyTrackginSetting = IsMSANISPOTEnabled || EnableDoubleVerify;

  const verifiedTrackingSettingsValidationProps = useMemo(
    () => {
      if (_.isNull(campaign) || _.isUndefined(campaign)) {
        return { data: null };
      }
      const VerifiedTrackingSettings = _.get(campaign, ['data', 'VerifiedTrackingSettings'], []);

      return {
        data: {
          VerifiedTrackingSettings,
        },
      };
    }
    , [campaign]
  );

  const shouldServeOnMSAN = campaignShouldServeOnMSAN(campaignSettings);
  const isEnableMSANDisplayCampaignsEnabled = EnableMSANDisplayCampaigns && isXandrDisplayAdsCampaign(campaign.data.CampaignSubType) && shouldServeOnMSAN;
  const isEnableMSANVideoCampaignsEnabled = EnableMSANVideoCampaigns && isXandrVideoAdsCampaign(campaign.data.CampaignSubType) && shouldServeOnMSAN;

  const [
    allCampaignSettingsSections,
    campaignSettingsExpandedSections,
    setCampaignSettingsExpandedSections,
  ] = useCollapseState(
    _.compact([
      'campaign',
      (!isEnableMSANDisplayCampaignsEnabled && !isEnableMSANVideoCampaignsEnabled) && 'budget',
      (!isEnableMSANDisplayCampaignsEnabled && !isEnableMSANVideoCampaignsEnabled) && 'bid-strategy',
      (isEnableMSANDisplayCampaignsEnabled || isEnableMSANVideoCampaignsEnabled) && 'bid-and-budget',
      shouldshowIASVerifiedImpressionTrackingURLs && 'impression-tracking-URLs',
      shouldShowThirdPartyTrackginSetting && 'third-party-measurement-settings',
    ]), /* all sections */
    _.compact([
      'campaign',
      (!isEnableMSANDisplayCampaignsEnabled && !isEnableMSANVideoCampaignsEnabled) && 'budget',
      (!isEnableMSANDisplayCampaignsEnabled && !isEnableMSANVideoCampaignsEnabled) && 'bid-strategy',
      (isEnableMSANDisplayCampaignsEnabled || isEnableMSANVideoCampaignsEnabled) && 'bid-and-budget',
    ]) /* initially expanded sections */
  );

  const basicSettingsExpand = useMemo(
    () => collapseExpander({ setActiveKeys: setCampaignSettingsExpandedSections }),
    [setCampaignSettingsExpandedSections]
  );

  const [
    allAdvancedCampaignSettingsSections,
    advancedCampaignSettingsExpandedSections,
    setAdvancedCampaignSettingsExpandedSections,
  ] = useCollapseState(_.compact([
    (isEnableMSANDisplayCampaignsEnabled || isEnableMSANVideoCampaignsEnabled) && 'web-exclusions',
  ]), ['']);

  const advancedSettingsExpand = useMemo(
    () => collapseExpander({ setActiveKeys: setAdvancedCampaignSettingsExpandedSections }),
    [setAdvancedCampaignSettingsExpandedSections]
  );

  const webExclusionsError = serverErrors && serverErrors.filter(err => err.property === 'webExclusions');
  const [moreOptionsSectionExpanded, setNameAndTitleExpanded] = useState(false);
  const onMoreOptionsSectionToggle = useCallback((expanded) => {
    setNameAndTitleExpanded(expanded);
  }, []);

  const minBudget = useMemo(() => {
    let dealMinimumBid = 0;
    if (dealDetails) {
      const exchangeRateMapping = getAccountExchangeRateFromLocalStorage(serverContext);
      dealMinimumBid = getConvertedCurrencyDealMinimumBid(dealDetails.MinimumBid, dealDetails.Currency, serverContext.CurrentAccount.CurrencyCode, exchangeRateMapping.val);
      return Math.max(dealMinimumBid || 0, MinXandrDailyBudgetRangePerCurrency[serverContext.CurrentAccount.CurrencyCode]);
    }
    return MinXandrDailyBudgetRangePerCurrency[serverContext.CurrentAccount.CurrencyCode];
  }, [dealDetails, serverContext]);
  const getBasicSettingsSection = () => (
    <div className={classNames('basic-campaign-section', classes.container)}>
      <Stack horizontal tokens={{ childrenGap: 8 }} >
        <Collapse allPanelItems={allCampaignSettingsSections} checkItems skipItemsTypeCheck activeKeys={campaignSettingsExpandedSections} setActiveKeys={setCampaignSettingsExpandedSections}>
          <ValidatedFixedPanel
            key="campaign"
            title={<TextTitle titleKey={_TL_('Campaign name')} topicKey="pop_BA_CampaignCreate_Settings_CampaignName" />}
            attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
          >
            <ServerValidatedRemainingCharsValidatedTextField
              placeholder={i18n.getString(_TL_('Enter a name for your campaign'))}
              id="input-campaign-name"
              validationProps={{
                field: 'Name',
                schema: Campaign,
              }}
              i18n={i18n}
              formErrorProps={{ filter: campaignNameProp }}
            />
          </ValidatedFixedPanel>
          {
            (isEnableMSANDisplayCampaignsEnabled || isEnableMSANVideoCampaignsEnabled) &&
            <ValidatedPanel
              key="bid-and-budget"
              title={
                <TextTitle
                  titleKey={_TL_('Budget and Bid Strategy')}
                  topicKey="pop_BA_Display_BidStrategy"
                />
              }
              attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
              actuationProps={{
                expand: basicSettingsExpand,
                expandKey: 'bid-and-budget',
              }}
            >
              {
                campaign && campaign.data ? (
                  <AudienceBudgetBidStrategy
                    data={campaign.data}
                    legacyI18n={legacyI18n}
                    campaignType="audience"
                    campaignSubType={campaign.data.CampaignSubType}
                    isEnableMSANDisplayCampaignsEnabled
                  />
                ) : (<SectionShimmer count={3} />)
              }
            </ValidatedPanel>
          }
          {
            (!isEnableMSANDisplayCampaignsEnabled && !isEnableMSANVideoCampaignsEnabled) &&
            <ValidatedFixedPanel
              key="budget"
              title={<TextTitle titleKey={_TL_('Budget')} />}
              attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
              actuationProps={{
                expand: basicSettingsExpand,
                expandKey: 'budget',
              }}
            >
              <ServerValidatedLegacyBudget
                className={classNames('vanilla-budget')}
                campaignId={campaign.Id || campaignId}
                validationDailyMinBudget={minBudget}
                hideBudgetMinRecommendation
                validationProps={{
                  data: { Budget: campaign.data.Budget },
                }}
              />
            </ValidatedFixedPanel>
          }
          {
            (!isEnableMSANDisplayCampaignsEnabled && !isEnableMSANVideoCampaignsEnabled) &&
            <ValidatedFixedPanel
              key="bid-strategy"
              title={<TextTitle
                titleKey={_TL_('Bid strategy')}
                topicKey={
                  isXandrDisplayAdsCampaign(campaign.data.CampaignSubType) ? 'pop_BA_Display_BidStrategy' : 'pop_BA_VideoAds_BidStrategyType'
                }
              />}
              attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
              actuationProps={{
                expand: basicSettingsExpand,
                expandKey: 'bid-strategy',
              }}
            >
              <span className="bid-strategy-type">
                {getBidTitleDetails(i18n, campaign.data.BiddingScheme)}
              </span>
            </ValidatedFixedPanel>
          }
          <ValidatedPanel
            key="impression-tracking-URLs"
            title={<TextTitle
              titleKey={_TL_('Third-party measurement')}
              topicKey="pop_BA_CampaignCreate_Settings_IAS"
              details={formatImpressionTrackingSetting(campaign.data.CampaignSettings, i18n)}
            />}
            actuationProps={{
              expand: basicSettingsExpand,
              expandKey: 'impression-tracking-URLs',
            }}
          >
            <ValidatedImpressionTrackingSetting
              i18n={i18n}
              campaignSettings={campaign.data.CampaignSettings}
            />
          </ValidatedPanel>
          <ValidatedPanel
            key="third-party-measurement-settings"
            title={<TextTitle
              titleKey={_TL_('Third-party measurement')}
              topicKey="pop_BA_Measurement_ThirdParty"
              details={formatVendorSettings({ verifiedTrackingSettingsValidationProps, i18n })}
              headingRole="heading"
              headingClassName="third-party-measurement-heading"
              headingLevel="2"
              as="span"
            />}
            attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
            actuationProps={{
              expand: basicSettingsExpand,
              expandKey: 'third-party-measurement',
            }}
          >
            <ThirdPartyMeasurementV3WithValidation
              i18n={i18n}
              validationProps={verifiedTrackingSettingsValidationProps}
              IsMSANISPOTEnabled={IsMSANISPOTEnabled}
              EnableDoubleVerify={EnableDoubleVerify}
              aid={aid}
              cid={cid}
            />
          </ValidatedPanel>
          {
            (isEnableMSANDisplayCampaignsEnabled || isEnableMSANVideoCampaignsEnabled) &&
            <ValidatedFixedPanel
              key="campaign"
              title={<TextTitle titleKey={_TL_('Conversion Goals')} topicKey="pop_BA_CampaignCreate_Settings_CampaignName" />}
              attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
            >
              <ValidatedConversionGoals
                campaign={campaign.data}
                validationProps={{
                              initialValue: campaign.data,
                              data: campaign.data,
                            }}
                cid={advertiserCustomerId}
                aid={aid}
              />
            </ValidatedFixedPanel>
          }
        </Collapse>
        {dealDetails && <DealImpressionPanel i18n={i18n} deal={dealDetails} serverContext={serverContext} />}
      </Stack>
      {
        allAdvancedCampaignSettingsSections && allAdvancedCampaignSettingsSections.length > 0 &&
        <Stack horizontal tokens={{ childrenGap: 8 }} >
          <ValidatedBootstrapPanel
            onToggle={onMoreOptionsSectionToggle}
            expanded={moreOptionsSectionExpanded}
            className={classNames(classes.collapsiblePanel, 'advanced-settings-panel')}
            render={() => (
              <>
                <BootstrapPanel.Heading>
                  <BootstrapPanel.Title toggle>
                    {i18n.getString(_TL_('Advanced settings'))}
                  </BootstrapPanel.Title>
                </BootstrapPanel.Heading>
                <BootstrapPanel.Collapse>
                  <BootstrapPanel.Body>
                    <div className={classNames('advanced-campaign-section')}>
                      <Collapse allPanelItems={allAdvancedCampaignSettingsSections} checkItems skipItemsTypeCheck activeKeys={advancedCampaignSettingsExpandedSections} setActiveKeys={setAdvancedCampaignSettingsExpandedSections}>
                        <ValidatedPanel
                          key="web-exclusions"
                          title={<TextTitle titleKey={_TL_('Web Exclusions')} />}
                          attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                          actuationProps={{
                            expand: advancedSettingsExpand,
                            expandKey: 'web-exclusions',
                          }}
                        >
                          <span className="web-exclusions-description">
                            {i18n.getString(_TL_('Which websites should not show your ads and which IP addresses should be blocked from seeing your ads?'))}
                          </span>
                          <ServerValidatedWebExclusionsWithValidation
                            showSiteExclusion
                            showIPExclusion
                            level={ScopeLevel.Campaign}
                            parentData={campaign && campaign.data}
                            exclusionData={exclusions || {}}
                            errors={webExclusionsError}
                            formErrorProps={{ filter: webExclusionPropertyVal }}
                          />
                        </ValidatedPanel>
                      </Collapse>
                    </div>
                  </BootstrapPanel.Body>
                </BootstrapPanel.Collapse>
              </>
            )}
          />
        </Stack>
      }
    </div>);

  return (
    <>
      <ActuationGroup actuationProps={{ isActive: areBeaconsActive }}>
        <AttentionBeaconGroup>
          <FormErrorGroup formErrorProps={{ errors: serverErrors, showAllValidationErrors }}>
            {!hideStepHeader &&
              <>
                <h1>{i18n.getString(_TL_('Campaign settings'))}</h1>
                <span className={classes.description}>{i18n.getString(_TL_('Tell us about your campaign and how much you want to spend.'))}</span>
              </>
            }
            <div className="campaign-server-errors">
              <ErrorMessages messages={campaignErrors} />
            </div>
            <div className="other-server-errors">
              <UnmappedFormErrors filter={campaignUrlOptionsProperties} />
            </div>
            <div className="system-errors">
              <ErrorMessages messages={systemErrors} />
            </div>
            <ValidationGroup
              validationProps={{
                schema: Campaign,
                onValidate: onCampaignValidation,
                data: campaign.data,
                i18n: legacyI18n,
              }}
            >
              {getBasicSettingsSection()}
            </ValidationGroup>
            {navigationConfirmationPrompt}
          </FormErrorGroup>
        </AttentionBeaconGroup>
      </ActuationGroup>
    </>
  );
}

StepCampaignSettings.propTypes = {
  stepService: PropTypes.shape({}).isRequired,
  scope: PropTypes.shape({
    values: PropTypes.shape({
      cid: PropTypes.number.isRequired,
      aid: PropTypes.number.isRequired,
      uid: PropTypes.number.isRequired,
      campaignId: PropTypes.number,
    }).isRequired,
  }).isRequired,
};

const withLoadingShell = withStepLoadingShell(StepCampaignSettings);

export { withLoadingShell as StepCampaignSettings };
